<script setup lang="ts">
import { watch, computed } from 'vue';

import {
  useBrowserStore,
  useCoreStore,
} from '@apps/main/new-desktop/core/stores';
import {
  RelativeMoneyFormatter,
  BracketType,
} from '@apps/main/new-desktop/core/components/formatters';

import {
  AppImagesLoader,
  AppLazyLoadComponent,
  AppSvgIcon,
  AppTag,
  AppLinkButton,
  AppTooltip,
  AppCircleCheckbox,
  AppFlicker,
} from '@libs/ui';
import { isFeatureToggleActive } from '@libs/feature-toggle';

import type { FerryProductCardData } from '../../../models';
import { FerryUpsellType } from '../../../models';
import { useUpsellStore } from '../../../stores';

import FerryProductCardUpsell from './FerryProductCardUpsell.vue';

interface FerryProductCardContentProps {
  productCardContent: FerryProductCardData;
  printMode: boolean;
}

const props = defineProps<FerryProductCardContentProps>();

interface FerryProductCardContentEmits {
  (event: 'change-upsell', upsellType: FerryUpsellType): void;
  (event: 'show-details'): void;
}

const emit = defineEmits<FerryProductCardContentEmits>();

const browserStore = useBrowserStore();
const coreStore = useCoreStore();
const upsellStore = useUpsellStore();

function onContentClick(): void {
  emit('show-details');
}

const isLoading = computed<boolean>(() => {
  if (props.productCardContent.upsell.ingredientId) {
    return upsellStore.isLoadingDictionary.has(
      props.productCardContent.upsell.ingredientId
    );
  }

  return false;
});

const allTickets = computed(() =>
  upsellStore.getTicketCategories(props.productCardContent.upsell.token)
);

const baseTicketPrice = computed<number>(() => {
  // FYI: For initial display of ticket category prices we need to use base price as seleted price is 0.
  // In new flow, we are no longer comparing to base price as old flow was showing relative price only for flexi tickets.
  // New flow needs to show both relative prices
  if (
    props.productCardContent.upsell.ticket.price !== undefined &&
    props.productCardContent.upsell.ticket.basePrice !== undefined
  ) {
    return props.productCardContent.upsell.ticket.price === 0
      ? props.productCardContent.upsell.ticket.basePrice
      : props.productCardContent.upsell.ticket.price;
  }
  return 0;
});

watch(
  props,
  () => {
    upsellStore.fetchTicketCategories(
      props.productCardContent.upsell.token,
      props.productCardContent.upsell.ingredientId
    );
  },
  { immediate: true }
);

function selectTicketCategory(category: string): void {
  upsellStore.currentFerryTicketCategoryEditionKey =
    props.productCardContent.upsell.token;
  upsellStore.currentFerryTicketEditionIngredientId =
    props.productCardContent.upsell.ingredientId;
  upsellStore.setUserSelectionFromModal(category);
}
</script>

<template>
  <div
    v-if="isFeatureToggleActive('ferry-card')"
    class="ferry-product-card-content-v2"
  >
    <div class="ferry-content">
      <div class="left-section">
        <div class="top-row">
          <div class="ferry-logo">
            <AppLazyLoadComponent class="logo">
              <AppImagesLoader
                :key="props.productCardContent.details.logo.urls.join('-')"
                :urls="props.productCardContent.details.logo.urls"
                alt="ferry carrier logo"
                img-type="scaleX"
                @images-loader-error="coreStore.handleImageLoadError"
              >
                <AppSvgIcon name="ferry" size="24" />
              </AppImagesLoader>
            </AppLazyLoadComponent>
          </div>
          <div @click="onContentClick" class="details">
            {{ $t('planner.details') }}
          </div>
        </div>
        <div class="destinations">
          {{ props.productCardContent.fromPort.country }}
          &mdash;
          {{ props.productCardContent.toPort.country }}
        </div>
      </div>

      <div class="middle-section">
        <div class="route-details">
          <div class="origin">
            {{ props.productCardContent.fromPort.time }}
            <div class="port">
              <span class="desktop-port-name">
                {{ props.productCardContent.fromPort.country }}
              </span>
            </div>
          </div>

          <div class="time">
            <div class="duration">
              {{ props.productCardContent.details.time }}
            </div>
            <div class="separator">
              <div class="line" />
              <div class="icon-wrapper">
                <AppSvgIcon name="ferry" size="13" color="color-white" />
              </div>
            </div>
            <div class="stops">
              {{ props.productCardContent.details.shipName }}
            </div>
          </div>

          <div class="destination">
            {{ props.productCardContent.toPort.time }}
            <div class="port">
              <span class="desktop-port-name">
                {{ props.productCardContent.toPort.country }}
              </span>
            </div>
          </div>
        </div>

        <div
          v-if="props.productCardContent.details.warningTags.length"
          class="warning-tags"
        >
          <AppTag
            v-for="(tag, index) in props.productCardContent.details.warningTags"
            :key="index"
            type="warning"
            :icon="{ name: tag.svgIconType, size: '18' }"
            class="tag-container"
          >
            <template #title>
              <span class="tag-description">
                {{ $t(tag.description) }}
              </span>
            </template>
          </AppTag>
        </div>

        <!-- TODO: move vahicle and cabin from upsell object -->
        <div class="ferry-details">
          <!-- vehicle -->
          <div
            v-if="
              props.productCardContent.upsell.vehicles.length ||
              props.productCardContent.upsell.allowVehicles
            "
            class="upsell-item"
            @click.stop="emit('change-upsell', FerryUpsellType.Vehicle)"
          >
            <div class="value">
              <div
                v-if="props.productCardContent.upsell.vehicles.length"
                class="vehicles"
              >
                {{
                  `${props.productCardContent.upsell.vehicles.length} ${$t(
                    'planner.vehicle'
                  )}`
                }}
              </div>
              <AppLinkButton
                v-if="
                  props.productCardContent.upsell.allowVehicles &&
                  !props.printMode
                "
                id="change-vehicle-category-button"
                class="edit-vehicles"
                :text="
                  props.productCardContent.upsell.vehicles.length
                    ? $t('planner.edit')
                    : $t('planner.transport.add_vehicle')
                "
                size="small"
                @click="emit('change-upsell', FerryUpsellType.Vehicle)"
              />
            </div>
          </div>

          <!-- cabin -->
          <div v-if="props.productCardContent.upsell.cabin" class="upsell-item">
            <div class="value">
              <span>{{ $t('planner.cabin_seat_included') }}</span>
              <AppTooltip
                :position="
                  browserStore.isSmallDevice ? 'bottom-left' : 'bottom-right'
                "
              >
                <template #trigger>
                  <AppSvgIcon name="infoCircle" size="18" />
                </template>
                <template #default>
                  {{ $t('planner.can_be_changed_in_later_step') }}
                </template>
              </AppTooltip>
            </div>
          </div>
        </div>

        <div class="open-ferry-details">
          <span @click="onContentClick">{{ $t('planner.details') }}</span>
        </div>
      </div>

      <div class="right-section">
        <div v-if="isLoading" class="ferry-upsell-loading">
          <div
            v-for="index in 2"
            :key="index"
            class="ferry-upsell-loading-item"
          >
            <div class="radio-loader">
              <AppFlicker
                height="spacing-16"
                width="spacing-16"
                border-radius="border-radius-8"
              />
            </div>
            <div class="ticket-loader">
              <AppFlicker
                height="spacing-16"
                width="spacing-96"
                border-radius="border-radius-8"
              />
              <AppFlicker
                height="spacing-16"
                width="spacing-64"
                border-radius="border-radius-8"
              />
            </div>
            <div class="radio-loader">
              <AppFlicker
                height="spacing-16"
                width="spacing-16"
                border-radius="border-radius-8"
              />
            </div>
          </div>
        </div>
        <div v-else class="ferry-upsell">
          <div
            v-for="ticket in allTickets"
            :key="ticket.token"
            class="ferry-ticket"
            :class="{
              flexi: ticket.type === 'FLEXI',
            }"
            @click="selectTicketCategory(ticket.type)"
          >
            <div class="ticket-radio">
              <AppCircleCheckbox
                :checked="
                  props.productCardContent.upsell.ticket.type === ticket.type
                "
                theme="radio"
              />
            </div>

            <div class="ticket-type">
              <span>
                {{ ticket.type }}
              </span>
              <template
                v-if="
                  ticket.price.amount &&
                  baseTicketPrice &&
                  ticket.price.amount !== baseTicketPrice
                "
              >
                <RelativeMoneyFormatter
                  :data="{
                    price: ticket.price.amount,
                    basePrice: baseTicketPrice,
                    color: 'color-secondary-40',
                    font: 'font-label-l-light',
                    bracketType: BracketType.None,
                  }"
                />
              </template>
              <template v-else>
                <span class="included">{{ $t('planner.included') }}</span>
              </template>
            </div>

            <div class="ticket-info">
              <AppTooltip
                :position="
                  browserStore.isSmallDevice ? 'bottom-left' : 'bottom-right'
                "
              >
                <template #trigger>
                  <AppSvgIcon name="infoCircle" size="18" />
                </template>
                <template #default>
                  {{ ticket.description }}
                </template>
              </AppTooltip>
            </div>
          </div>
          <div v-if="allTickets.length === 0" class="ferry-ticket">
            <div class="ticket-radio">
              <AppCircleCheckbox :checked="true" theme="radio" />
            </div>
            <div class="ticket-type">
              <span>{{ props.productCardContent.upsell.ticket.type }}</span>
              <span class="included">{{ $t('planner.included') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="ferry-product-card-content" @click="onContentClick">
    <div class="ferry-content">
      <div class="left-section">
        <div class="ferry-logo">
          <AppLazyLoadComponent class="logo">
            <AppImagesLoader
              :key="props.productCardContent.details.logo.urls.join('-')"
              :urls="props.productCardContent.details.logo.urls"
              alt="ferry carrier logo"
              img-type="contain"
              @images-loader-error="coreStore.handleImageLoadError"
            >
              <AppSvgIcon name="ferry" size="24" />
            </AppImagesLoader>
          </AppLazyLoadComponent>
        </div>
        <div class="destinations">
          {{ props.productCardContent.fromPort.country }}
          &mdash;
          {{ props.productCardContent.toPort.country }}
        </div>
      </div>
      <div class="right-section">
        <div class="details">
          <div class="origin">
            {{ props.productCardContent.fromPort.time }}
            <div class="port">
              <span class="desktop-port-name">
                {{ props.productCardContent.fromPort.country }}
              </span>
            </div>
          </div>

          <div class="time">
            <div class="duration">
              {{ props.productCardContent.details.time }}
            </div>
            <div class="separator">
              <div class="line" />
              <div class="icon-wrapper">
                <AppSvgIcon name="ferry" size="13" color="color-white" />
              </div>
            </div>
            <div class="stops">
              {{ props.productCardContent.details.mainDetails }}
            </div>
          </div>

          <div class="destination">
            {{ props.productCardContent.toPort.time }}
            <div class="port">
              <span class="desktop-port-name">
                {{ props.productCardContent.toPort.country }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-if="
            browserStore.isSmallDevice &&
            props.productCardContent.upsell.transportationName
          "
          class="transport"
        >
          <span class="transport-name">
            {{ props.productCardContent.upsell.transportationName }}
          </span>
        </div>
        <div
          v-if="props.productCardContent.details.warningTags.length"
          class="warning-tags"
        >
          <AppTag
            v-for="(tag, index) in props.productCardContent.details.warningTags"
            :key="index"
            type="warning"
            :icon="{ name: tag.svgIconType, size: '18' }"
            class="tag-container"
          >
            <template #title>
              <span class="tag-description">
                {{ $t(tag.description) }}
              </span>
            </template>
          </AppTag>
        </div>
        <div class="upsell-item">
          <FerryProductCardUpsell
            :vehicles="props.productCardContent.upsell.vehicles"
            :ticket="props.productCardContent.upsell.ticket"
            :cabin="props.productCardContent.upsell.cabin"
            :token="props.productCardContent.upsell.token"
            :ingredient-id="props.productCardContent.upsell.ingredientId"
            :allow-vehicles="props.productCardContent.upsell.allowVehicles"
            :transportation-name="
              props.productCardContent.upsell.transportationName
            "
            :print-mode="props.printMode"
            @change-upsell="emit('change-upsell', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ferry-product-card-content-v2 {
  --color-selected-ticket: #f2faf4;
  padding: var(--spacing-16);
  @include for-small-devices-max {
    padding: var(--spacing-12);
  }
  .ferry-content {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;

    @include for-small-devices-max {
      flex-direction: column;
      gap: var(--spacing-16);
    }

    .left-section {
      display: flex;
      flex-direction: column;
      @include for-small-devices-max {
        width: 100%;
        gap: var(--spacing-12);
      }
      .top-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        .ferry-logo {
          height: 30px;
          box-sizing: content-box;
        }

        .details {
          display: none;

          @include for-small-devices-max {
            display: flex;
            @include font-label-l-light;
            text-decoration: underline;
            cursor: pointer;
          }
        }
      }

      .destinations {
        display: none;
        @include font-label-s-light;
        @include for-small-devices-max {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--color-secondary-60);
        }
      }
    }

    .middle-section {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-12);

      @include for-small-devices-max {
        width: 100%;
        gap: var(--spacing-16);
      }

      .route-details {
        --mid-block-width: 100px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-16);
        width: 100%;
        @include for-small-devices-max {
          padding: 0;
        }

        .origin,
        .destination {
          display: flex;
          flex-direction: column;
          // align origin and destination columns mid block width
          flex-basis: calc(50%);
          gap: var(--spacing-4);
          @include font-label-xxl-bold;
          span {
            @include font-label-l-light;
          }

          .port {
            color: var(--color-secondary-60);
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            gap: var(--spacing-4);
            .desktop-port-name {
              display: block;
              @include for-small-devices-max {
                display: none;
              }
            }
          }
        }
        .origin {
          align-items: flex-end;
          .desktop-port-name {
            text-align: end;
          }
          .port {
            justify-content: end;
          }
        }
        .destination {
          align-items: flex-start;
        }

        .time {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100px;

          .duration {
            @include font-label-s-light;
            color: var(--color-secondary-60);
            padding-bottom: var(--spacing-12);
            @include for-small-devices-max {
              padding-bottom: var(--spacing-4);
            }
          }
          .separator {
            position: relative;
            width: 100%;
            height: var(--spacing-24);

            .line {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 100%;
              height: 1px;
              background: rgb(255, 255, 255);
              background: linear-gradient(
                90deg,
                rgba(255, 255, 255, 1) 0%,
                rgba(36, 30, 61, 1) 50%,
                rgba(255, 255, 255, 1) 100%
              );
            }
            .icon-wrapper {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              background: var(--color-primary-5);
              border-radius: var(--border-radius-20);
              padding: var(--spacing-4);
            }
          }
          .stops {
            color: var(--color-secondary-60);
            @include font-label-s-light;
            text-align: center;
            white-space: nowrap;
          }
        }
      }

      .warning-tags {
        display: flex;
        justify-content: center;
      }

      .ferry-details {
        display: flex;
        align-items: center;
        justify-content: center;

        @include for-small-devices-max {
          justify-content: space-between;
        }

        .upsell-item {
          display: flex;
          justify-content: flex-start;
          padding: 0 var(--spacing-16);
          border-right: solid 1px var(--color-overlay);
          @include for-small-devices-max {
            border-right: none;
            padding: 0;
          }

          .value {
            display: flex;
            justify-content: flex-start;
            align-items: center;
            flex-direction: row;
            gap: var(--spacing-8);
            @include font-label-s-light;
            color: var(--color-secondary-60);

            @include for-small-devices-max {
              justify-content: space-between;
            }

            .description {
              display: flex;
              justify-content: flex-start;
              align-items: center;
              flex-direction: row;
              gap: var(--spacing-8);
            }

            .vehicles {
              display: flex;
              flex-direction: row;
              flex-wrap: wrap;
              white-space: wrap;

              @include for-small-devices-max {
                gap: var(--spacing-4);
                flex-direction: row;
                align-items: start;
              }
            }

            .edit-vehicles {
              color: var(--color-secondary-60);
            }
          }
        }
        .upsell-item:last-child {
          border-right: none;
        }
      }

      .open-ferry-details {
        display: flex;
        align-items: center;
        justify-content: center;
        @include for-small-devices-max {
          display: none;
        }
        span {
          @include font-label-xl-light;
          text-decoration: underline;
          cursor: pointer;
        }
      }
    }

    .right-section {
      padding-left: var(--spacing-16);
      border-left: 1px dashed var(--color-overlay);
      align-self: stretch;
      width: 200px;
      display: flex;
      align-items: center;

      @include for-small-devices-max {
        border-left: none;
        border-top: 1px dashed var(--color-overlay);
        padding-top: var(--spacing-8);
        padding-left: 0;
        width: 100%;
        .ferry-upsell {
          gap: var(--spacing-4);
        }
      }
      .ferry-upsell {
        display: flex;
        flex-direction: column;
        align-self: stretch;
        width: 100%;
        justify-content: center;
        .ferry-ticket {
          display: flex;
          gap: var(--spacing-12);
          padding: var(--spacing-12);
          align-items: center;
          justify-content: space-between;
          border-radius: var(--border-radius-8);
          cursor: pointer;
          &.flexi {
            background: var(--color-selected-ticket);
          }

          .ticket-type {
            display: flex;
            flex: 1;
            flex-direction: column;
            justify-content: center;
            gap: var(--spacing-4);
            @include font-label-s-light;
            color: var(--color-secondary-60);

            .included {
              color: var(--color-secondary-40);
              @include font-label-l-bold;
            }
          }
        }
      }

      .ferry-upsell-loading {
        display: flex;
        flex-direction: column;
        align-self: stretch;
        width: 100%;
        justify-content: center;

        .ferry-upsell-loading-item {
          display: flex;
          gap: var(--spacing-12);
          padding: var(--spacing-12);
          align-items: center;
          justify-content: space-between;
          border-radius: var(--border-radius-8);

          .ticket-loader {
            display: flex;
            flex: 1;
            flex-direction: column;
            justify-content: center;
            gap: var(--spacing-4);
          }
        }
      }
    }
  }
}

.ferry-product-card-content {
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: auto;

  @include for-small-devices-max {
    grid-template-rows: auto auto;
    grid-template-columns: auto;
  }

  .warning-tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-4);
    justify-content: center;
    padding-bottom: var(--spacing-12);

    .tag-description {
      @include font-label-s-light;
    }
  }

  .ferry-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: fit-content;
    flex-wrap: wrap;

    @include for-small-devices-max {
      border-right: unset;
    }

    .left-section {
      display: flex;
      flex-direction: column;
      align-items: start;
      gap: var(--spacing-8);
      padding: var(--spacing-16) var(--spacing-16) 0 var(--spacing-16);
      @include for-small-devices-max {
        padding: 0;
        padding-top: var(--spacing-16);
        align-items: center;
      }
      .ferry-logo {
        height: 30px;
        box-sizing: content-box;

        @include for-small-devices-max {
          padding: 0;
        }
      }
      .destinations {
        display: none;
        @include font-label-s-light;
        @include for-small-devices-max {
          display: flex;
          color: var(--color-secondary-60);
        }
      }
    }
    .right-section {
      flex-grow: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      padding: 0 var(--spacing-16) var(--spacing-16) var(--spacing-16);

      @include for-small-devices-max {
        padding: var(--spacing-16) 0 0;
      }

      .details {
        --mid-block-width: 100px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-16);
        width: 100%;
        padding-bottom: var(--spacing-12);
        @include for-small-devices-max {
          padding: 0;
        }

        .origin,
        .destination {
          display: flex;
          flex-direction: column;
          // align origin and destination columns mid block width
          flex-basis: calc(50%);
          gap: var(--spacing-4);
          @include font-label-xxl-bold;
          span {
            @include font-label-l-light;
          }

          .port {
            color: var(--color-secondary-60);
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            gap: var(--spacing-4);
            .desktop-port-name {
              display: block;
              @include for-small-devices-max {
                display: none;
              }
            }
          }
        }
        .origin {
          align-items: flex-end;
          .desktop-port-name {
            text-align: end;
          }
          .port {
            justify-content: end;
          }
        }
        .destination {
          align-items: flex-start;
        }

        .time {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100px;

          .duration {
            @include font-label-s-light;
            color: var(--color-secondary-60);
            padding-bottom: var(--spacing-12);
          }
          .separator {
            position: relative;
            width: 100%;
            height: var(--spacing-24);

            .line {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 100%;
              height: 1px;
              background: rgb(255, 255, 255);
              background: linear-gradient(
                90deg,
                rgba(255, 255, 255, 1) 0%,
                rgba(36, 30, 61, 1) 50%,
                rgba(255, 255, 255, 1) 100%
              );
            }
            .icon-wrapper {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              background: var(--color-primary-5);
              border-radius: var(--border-radius-20);
              padding: var(--spacing-4);
            }
          }
          .stops {
            color: var(--color-secondary-60);
            @include font-label-s-light;
            text-align: center;
            white-space: nowrap;
          }
        }
      }
      .transport {
        padding-bottom: var(--spacing-24);
        .transport-name {
          @include font-label-s-light;
          color: var(--color-secondary-60);
        }
      }

      .upsell-item {
        @include for-small-devices-max {
          padding: var(--spacing-12);
          width: 100%;
          border-top: 1px solid var(--color-secondary-93);
        }
      }
    }
  }

  .ferry-upsell {
    border-top: 1px solid var(--color-secondary-93);
  }
}
</style>
